import type { ZodError } from "zod";

import { parseUsd } from "../../shared/billing-money";
import {
  BillingTermsInputSchema,
  type BillingCadence,
  type BillingLineInput,
  type BillingLineKind,
  type BillingTerms,
  type BillingTermsInput,
} from "../../shared/billing-schemas";
import { dayToSeconds } from "./billing-format";
import {
  fieldErrorsFrom,
  REASON_REQUIREMENT,
  type FieldErrors,
  type FieldGuide,
} from "./billing-validation";

/**
 * What the terms form holds while an employee types (ADR-0073), and how it becomes the input
 * the server parses.
 *
 * Amounts are typed in dollars and parsed to exact cents before anything leaves the form; a third
 * decimal is refused rather than rounded. The finished draft runs through the same schema the
 * server uses, so the error an employee sees is the one the server would have given — every
 * error at once, each against the field it belongs to.
 */

export type DiscountKind = "" | "percent" | "fixed";

export type LineDraft = {
  key: number;
  kind: BillingLineKind;
  description: string;
  amount: string;
  quantity: string;
  cadence: BillingCadence;
  discountType: DiscountKind;
  discountValue: string;
};

export type TermsDraft = {
  title: string;
  toolId: string;
  effective: string;
  ends: string;
  notes: string;
  discountType: DiscountKind;
  discountValue: string;
  lines: LineDraft[];
  reason: string;
};

let lineKeys = 0;

export function blankLine(kind: BillingLineKind = "one_time"): LineDraft {
  lineKeys += 1;
  return {
    key: lineKeys,
    kind,
    description: "",
    amount: "",
    quantity: "1",
    cadence: "month",
    discountType: "",
    discountValue: "",
  };
}

function discountValueText(discount: BillingTerms["discount"]): string {
  if (discount === null) return "";
  return discount.type === "percent"
    ? (discount.basisPoints / 100).toString()
    : (discount.amountMinor / 100).toFixed(2);
}

function lineFromTerms(line: BillingTerms["lines"][number]): LineDraft {
  lineKeys += 1;
  return {
    key: lineKeys,
    kind: line.kind,
    description: line.description,
    amount: (line.unitAmountMinor / 100).toFixed(2),
    quantity: String(line.quantity),
    cadence: line.cadence ?? "month",
    discountType: line.discount?.type ?? "",
    discountValue: discountValueText(line.discount),
  };
}

/** The commercial half of a terms version or a quote: what a draft is pre-filled from. */
export type CommercialSource = Pick<
  BillingTerms,
  "title" | "tool" | "effectiveAt" | "endsAt" | "notes" | "discount" | "lines"
>;

/** A blank draft, or one pre-filled from an existing version for editing or revising. */
export function draftFrom(initial: CommercialSource | null): TermsDraft {
  if (initial === null) {
    return {
      title: "",
      toolId: "",
      effective: "",
      ends: "",
      notes: "",
      discountType: "",
      discountValue: "",
      lines: [blankLine()],
      reason: "",
    };
  }
  return {
    title: initial.title,
    toolId: initial.tool?.id ?? "",
    effective: new Date(initial.effectiveAt * 1_000).toISOString().slice(0, 10),
    ends:
      initial.endsAt === null ? "" : new Date(initial.endsAt * 1_000).toISOString().slice(0, 10),
    notes: initial.notes ?? "",
    discountType: initial.discount?.type ?? "",
    discountValue: discountValueText(initial.discount),
    lines: initial.lines.map(lineFromTerms),
    reason: "",
  };
}

type Discount = BillingTermsInput["discount"];

const TERMS_REQUIREMENTS: Record<string, string> = {
  title: "2 to 160 characters: how this arrangement is referred to internally.",
  tool: "Leave as the whole organization for a platform fee or a build fee before the tool exists.",
  effective: "The day the terms apply from, as a date.",
  ends: "Optional. Not before the effective date.",
  discountValue:
    "Percent: above 0, up to 100, on every charge. Fixed: dollars and cents, no more than the one-time charges.",
  notes: "Optional, up to 4000 characters. The customer never sees it.",
  reason: REASON_REQUIREMENT,
};

const LINE_REQUIREMENTS: Record<string, string> = {
  description: "2 to 200 characters, as it will read on the invoice.",
  amount: "Dollars and cents, like 250.00, with no more than two decimals. 0.00 is allowed.",
  quantity: "A whole number, at least 1.",
  cadence: "Monthly or annual.",
  discountValue:
    "Percent: above 0, up to 100. Fixed: dollars and cents, no more than the line it comes off.",
};

const TERMS_FIELD_NAMES: Record<string, string> = {
  toolId: "tool",
  effectiveAt: "effective",
  endsAt: "ends",
  discount: "discountValue",
  lines: "form",
  supersedesTermsId: "form",
};

const LINE_FIELD_NAMES: Record<string, string> = {
  unitAmountMinor: "amount",
  discount: "discountValue",
};

/** The form's name for a line field: the line's own key, never its position, which changes. */
export function lineField(key: number, name: string): string {
  return `line-${key}-${name}`;
}

/** The form's name for a schema field of the terms, for the lines the draft currently has. */
export function termsFieldName(draft: TermsDraft): (schemaField: string) => string {
  return (schemaField) => {
    const match = /^lines\.(\d+)\.(\w+)$/.exec(schemaField);
    if (match?.[1] !== undefined && match[2] !== undefined) {
      const key = draft.lines[Number(match[1])]?.key ?? 0;
      return lineField(key, LINE_FIELD_NAMES[match[2]] ?? match[2]);
    }
    return TERMS_FIELD_NAMES[schemaField] ?? schemaField;
  };
}

/** What each field of the terms form requires, for the lines the draft currently has. */
export function termsGuide(draft: TermsDraft): FieldGuide {
  const requirements = { ...TERMS_REQUIREMENTS };
  for (const line of draft.lines) {
    for (const [name, requirement] of Object.entries(LINE_REQUIREMENTS)) {
      requirements[lineField(line.key, name)] = requirement;
    }
  }
  return { rename: termsFieldName(draft), requirements };
}

function discountOf(type: DiscountKind, value: string): Discount | "invalid" {
  if (type === "") return null;
  if (type === "percent") {
    const percent = Number(value.trim());
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) return "invalid";
    return { type: "percent", basisPoints: Math.round(percent * 100) };
  }
  const minor = parseUsd(value);
  return minor === null || minor === 0 ? "invalid" : { type: "fixed", amountMinor: minor };
}

function discountMessage(type: DiscountKind): string {
  return type === "percent"
    ? "Enter a percentage above 0 and up to 100."
    : "Enter a dollar amount above 0, with no more than two decimals.";
}

function buildLine(line: LineDraft, errors: FieldErrors): BillingLineInput {
  const unitAmountMinor = parseUsd(line.amount);
  if (unitAmountMinor === null) {
    errors[lineField(line.key, "amount")] = "Enter an exact dollar amount.";
  }
  const quantity = Number(line.quantity);
  if (!Number.isInteger(quantity) || quantity < 1) {
    errors[lineField(line.key, "quantity")] = "Quantity must be a whole number, at least 1.";
  }
  const discount = discountOf(line.discountType, line.discountValue);
  if (discount === "invalid") {
    errors[lineField(line.key, "discountValue")] = discountMessage(line.discountType);
  }
  // A fixed discount is measured against the amount; until that parses, there is nothing to measure.
  const measurable =
    discount !== "invalid" && (discount?.type !== "fixed" || unitAmountMinor !== null);
  return {
    kind: line.kind,
    description: line.description.trim(),
    unitAmountMinor: unitAmountMinor ?? 0,
    quantity,
    cadence: line.kind === "recurring" ? line.cadence : null,
    discount: measurable ? discount : null,
  };
}

function buildDates(
  draft: TermsDraft,
  errors: FieldErrors,
): { effectiveAt: number; endsAt: number | null } {
  const effectiveAt = dayToSeconds(draft.effective);
  if (effectiveAt === null) errors["effective"] = "Enter the date the terms take effect.";
  const endsAt = draft.ends.trim() === "" ? null : dayToSeconds(draft.ends);
  if (draft.ends.trim() !== "" && endsAt === null) errors["ends"] = "The end date is not a date.";
  return { effectiveAt: effectiveAt ?? 0, endsAt };
}

/** The commercial fields as the server reads them, before the schema has passed judgement. */
export type TermsCandidate = Omit<BillingTermsInput, "supersedesTermsId">;

/**
 * The draft as the server would read it, plus what only the form can tell is wrong: a date or an
 * amount that never parsed. Such a field carries a placeholder the schema rejects too; when the
 * two verdicts are merged, the form's own message wins because it saw the text as typed.
 */
export function assembleTerms(draft: TermsDraft): {
  candidate: TermsCandidate;
  errors: FieldErrors;
} {
  const errors: FieldErrors = {};
  const dates = buildDates(draft, errors);
  const discount = discountOf(draft.discountType, draft.discountValue);
  if (discount === "invalid") errors["discountValue"] = discountMessage(draft.discountType);
  const lines = draft.lines.map((line) => buildLine(line, errors));
  // A fixed discount comes off the one-time charges; until every amount parses, it cannot be measured.
  const amountsParsed = draft.lines.every((line) => parseUsd(line.amount) !== null);
  const measurable = discount !== "invalid" && (discount?.type !== "fixed" || amountsParsed);
  return {
    candidate: {
      toolId: draft.toolId === "" ? null : draft.toolId,
      title: draft.title.trim(),
      ...dates,
      notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
      discount: measurable ? discount : null,
      lines,
      reason: draft.reason.trim(),
    },
    errors,
  };
}

/**
 * Every error at once. The form's own verdicts come first, in its field names; the schema's keep
 * the schema's path for the form to rename, except where they would land on a field the form has
 * already judged from the text as typed — a placeholder's failure says nothing the form did not.
 */
export function mergeErrors(
  parsed: { success: true } | { success: false; error: ZodError },
  own: FieldErrors,
  rename: (schemaField: string) => string,
): FieldErrors {
  const merged: FieldErrors = { ...own };
  if (parsed.success) return merged;
  for (const [schemaField, message] of Object.entries(fieldErrorsFrom(parsed.error))) {
    if (!(rename(schemaField) in own)) merged[schemaField] = message;
  }
  return merged;
}

/** Everything typed, as the server would read it, or everything wrong with it, by field. */
export function buildTermsInput(
  draft: TermsDraft,
  supersedesTermsId: string | null,
): { input: BillingTermsInput } | { errors: FieldErrors } {
  const { candidate, errors } = assembleTerms(draft);
  const parsed = BillingTermsInputSchema.safeParse(
    { ...candidate, supersedesTermsId },
    { reportInput: true },
  );
  if (parsed.success && Object.keys(errors).length === 0) return { input: parsed.data };
  return { errors: mergeErrors(parsed, errors, termsFieldName(draft)) };
}
